import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { ScoreRing } from '@/components/ScoreRing';
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';
import { ChevronLeft, Dumbbell, UtensilsCrossed, Heart, Brain, Moon, Timer, Utensils, Activity, PersonStanding } from 'lucide-react';

export default function ScoreBreakdown() {
  const nav = useNavigate();
  const [score, setScore] = useState(null);
  const [rec, setRec] = useState(null);

  useEffect(() => {
    api.get('/regen-score').then(r => setScore(r.data));
    api.get('/recovery-score').then(r => setRec(r.data));
  }, []);

  const w = score?.weights || {};
  const pillars = [
    { label: 'Training', val: score?.training ?? 0, weight: w.training, icon: Dumbbell, color: '#00C8FF' },
    { label: 'Nutrition', val: score?.nutrition ?? 0, weight: w.nutrition, icon: UtensilsCrossed, color: '#65A30D' },
    { label: 'Recovery', val: score?.recovery ?? 0, weight: w.recovery, icon: Heart, color: '#A78BFA' },
    { label: 'Mindset', val: score?.mindset ?? 0, weight: w.mindset, icon: Brain, color: '#FFB800' },
  ];
  const rw = rec?.weights || {};
  const recParts = [
    { label: 'Sleep', val: rec?.sleep ?? 0, weight: rw.sleep, icon: Moon, color: '#A78BFA' },
    { label: 'Fasting', val: rec?.fasting ?? 0, weight: rw.fasting, icon: Timer, color: '#00C8FF' },
    { label: 'Meal Timing', val: rec?.meal_timing ?? 0, weight: rw.meal_timing, icon: Utensils, color: '#FFB800' },
    { label: 'Activities', val: rec?.activities ?? 0, weight: rw.activities, icon: Activity, color: '#65A30D' },
  ];

  return (
    <div className="space-y-5" data-testid="score-breakdown-page">
      <div className="flex items-center gap-3">
        <button onClick={() => nav(-1)} data-testid="back-btn" className="w-9 h-9 rounded-full bg-[var(--c-film)] flex items-center justify-center"><ChevronLeft className="w-5 h-5" /></button>
        <h1 className="font-display font-black text-2xl">YourReGen Score</h1>
      </div>

      <div className="regen-card-elevated p-6 flex flex-col items-center blueprint-grid">
        <ScoreRing value={score?.score ?? 0} color='#65A30D' size={170} stroke={14} sub={score?.level} />
        {score && (
          <div className={`mt-3 font-mono font-bold ${(score.trend ?? 0) >= 0 ? 'text-[var(--c-green-ink)]' : 'text-[#FF5C5C]'}`}>
            {(score.trend ?? 0) >= 0 ? '+' : ''}{score.trend ?? 0} <span className="text-muted-foreground font-sans text-xs">vs yesterday</span>
          </div>
        )}
      </div>

      {/* 7-day trend */}
      <div className="regen-card-elevated p-5">
        <div className="label-mini mb-2">7-Day Trend</div>
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={score?.series || []} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <XAxis dataKey="dow" tick={{ fontSize: 11, fill: '#7E8C9E' }} axisLine={false} tickLine={false} />
              <YAxis domain={[40, 100]} tick={{ fontSize: 10, fill: '#7E8C9E' }} axisLine={false} tickLine={false} width={28} />
              <Tooltip contentStyle={{ background: '#101E30', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, fontSize: 12 }} />
              <Line type="monotone" dataKey="score" stroke='#65A30D' strokeWidth={2.5} dot={{ r: 3, fill: '#65A30D' }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Pillar contributions */}
      <div className="regen-card-elevated p-5">
        <div className="label-mini mb-3">Pillar Breakdown</div>
        <div className="space-y-3">
          {pillars.map(p => <Bar key={p.label} {...p} />)}
        </div>
      </div>

      <TrainingDetail t={score?.training_detail} weekly={score?.training} />

      {/* Recovery sub-breakdown */}
      <div className="regen-card-elevated p-5">
        <div className="label-mini mb-3">Inside Recovery</div>
        <div className="space-y-3">
          {recParts.map(p => <Bar key={p.label} {...p} />)}
        </div>
      </div>
    </div>
  );
}

function TrainingDetail({ t, weekly }) {
  if (!t?.today) return null;
  const d = t.today;
  return (
    <div className="regen-card-elevated p-5" data-testid="training-detail">
      <div className="flex items-center justify-between mb-3">
        <div className="label-mini">Inside Training · Today</div>
        <div className="font-mono text-sm" data-testid="training-today-score">{d.score}</div>
      </div>
      <div className="space-y-3">
        {d.gym_day && <Bar label="Gym" val={d.gym_pct ?? 0} weight={d.gym_weight} icon={Dumbbell} color="#00C8FF" />}
        <Bar label="Brisk walk" val={d.walk_pct} weight={d.walk_weight} icon={PersonStanding} color="#65A30D" />
      </div>
      <p className="text-xs text-muted-foreground mt-3" data-testid="training-today-note">
        {d.gym_day
          ? `Gym day: ${d.gym_pct ?? 0}% of ${d.sessions} session${d.sessions > 1 ? 's' : ''} done, brisk walk ${d.brisk.toLocaleString()}/${d.brisk_target.toLocaleString()} steps.`
          : `Rest day: your brisk walk is today's full Training Score (${d.brisk.toLocaleString()}/${d.brisk_target.toLocaleString()} steps).`}
        {' '}This is your live score for today and resets at midnight. This week so far: {t.week_score ?? 0}.
      </p>
      {(t.recent_days || []).length > 1 && (
        <div className="mt-3 grid grid-cols-7 gap-1" data-testid="training-history">
          {t.recent_days.map(x => (
            <div key={x.date} className={`rounded-lg p-1.5 text-center ${x.date === d.date ? 'bg-[#00C8FF]/15' : 'bg-[var(--c-film)]'}`} data-testid={`training-history-${x.date}`}>
              <div className="text-[9px] text-muted-foreground">{x.date === d.date ? 'Today' : new Date(`${x.date}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short' })}</div>
              <div className="font-mono text-xs">{x.score}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Bar({ label, val, weight, icon: Icon, color }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 flex-shrink-0" style={{ color }} />
      <div className="w-24 text-sm">{label}</div>
      <div className="flex-1 h-2 rounded-full bg-[var(--c-film)] overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${val}%`, background: color, boxShadow: `0 0 8px ${color}` }} />
      </div>
      <div className="font-mono text-sm w-8 text-right">{val}</div>
      {weight != null && <div className="text-[10px] text-muted-foreground w-10 text-right">{weight}%</div>}
    </div>
  );
}
